import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";

interface ProfileDropdownProps {
  name: string;
  email: string;
}

function ProfileDropdown({ name, email }: ProfileDropdownProps) {
  const navigate = useNavigate();
  const { logout, trialStatus } = useUser();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const initial = (name || "U").charAt(0).toUpperCase();

  const tierName =
    trialStatus?.plan === "business"
      ? "Business"
      : trialStatus?.plan === "enterprise"
      ? "Enterprise"
      : trialStatus?.plan === "pro"
      ? "Pro"
      : "Free";

  const planLabel = trialStatus?.hasSubscription
    ? tierName
    : trialStatus?.isTrialing
    ? `Trial · ${trialStatus.daysRemaining}d left`
    : trialStatus?.trialExpired
    ? "Trial ended"
    : "Free";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger pill */}
      <button
        className="dashboard-header-settings"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="dashboard-header-avatar">{initial}</span>
        <span className="dashboard-header-meta">
          <span className="dashboard-header-name">{name || "Account"}</span>
          <span className="dashboard-header-email">{email || ""}</span>
        </span>
        <span
          className="dashboard-header-chevron"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
        >
          &#9662;
        </span>
      </button>

      {/* Dropdown menu */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 220,
            background: "#ffffff",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            zIndex: 100,
            overflow: "hidden",
            animation: "profileDropIn 0.12s ease",
          }}
        >
          {/* User info header */}
          <div
            style={{
              padding: "14px 16px 10px",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                marginBottom: 2,
              }}
            >
              {name || "Account"}
            </div>
            <div style={{ fontSize: 11.5, color: "#6b7280" }}>
              {email}
            </div>
            <div
              style={{
                display: "inline-block",
                marginTop: 6,
                padding: "2px 8px",
                borderRadius: 999,
                fontSize: 10.5,
                fontWeight: 700,
                background:
                  trialStatus?.hasSubscription
                    ? "#ecfdf3"
                    : trialStatus?.trialExpired
                    ? "#fef3c7"
                    : "#eff6ff",
                color:
                  trialStatus?.hasSubscription
                    ? "#065f46"
                    : trialStatus?.trialExpired
                    ? "#92400e"
                    : "#1e40af",
              }}
            >
              {planLabel}
            </div>
          </div>

          {/* Menu items */}
          <div style={{ padding: "4px 0" }}>
            <DropdownItem
              label="Account settings"
              onClick={() => {
                setOpen(false);
                navigate("/profile");
              }}
            />
            <DropdownItem
              label="Document status"
              onClick={() => {
                setOpen(false);
                navigate("/document-status");
              }}
            />
            <DropdownItem
              label="Templates"
              onClick={() => {
                setOpen(false);
                navigate("/templates");
              }}
            />
            {!trialStatus?.hasSubscription && (
              <DropdownItem
                label="Subscribe"
                accent
                onClick={() => {
                  setOpen(false);
                  navigate("/profile");
                }}
              />
            )}
          </div>

          {/* Logout */}
          <div
            style={{
              borderTop: "1px solid #f1f5f9",
              padding: "4px 0",
            }}
          >
            <DropdownItem
              label="Log out"
              danger
              onClick={() => {
                setOpen(false);
                logout();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownItem({
  label,
  onClick,
  danger,
  accent,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        padding: "9px 16px",
        background: "none",
        border: "none",
        textAlign: "left",
        fontSize: 12.5,
        fontWeight: accent ? 700 : 500,
        color: danger ? "#dc2626" : accent ? "#2563eb" : "#374151",
        cursor: "pointer",
        transition: "background 0.1s ease",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = "#f9fafb")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = "transparent")
      }
    >
      {label}
    </button>
  );
}

export default ProfileDropdown;
